import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Hero from '../../components/Hero';
import SectionCard from '../../components/SectionCard';
import StatCard from '../../components/StatCard';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import { money } from '../../utils/format';

export default function SuperDashboard() {
  const { profile } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [data, setData] = useState({ institutes: [], plans: [], subscriptions: [], supportTickets: [] });
  useEffect(() => {
    let active = true;
    Promise.all(['institutes', 'plans', 'subscriptions', 'supportTickets'].map(async (name) => {
      try { const snap = await getDocs(collection(db, name)); return [name, snap.docs.filter((d) => !d.id.startsWith('__') && d.data().__system !== true).map((d) => ({ id: d.id, ...d.data() }))]; }
      catch { return [name, []]; }
    })).then((pairs) => active && setData(Object.fromEntries(pairs)));
    return () => { active = false; };
  }, []);

  const activeCenters = data.institutes.filter((x) => x.status === 'active').length;
  const activeSubscriptions = data.subscriptions.filter((x) => x.status === 'active').length;
  const mrr = useMemo(() => data.subscriptions.filter((x) => x.status === 'active').reduce((sum, item) => sum + Number(item.monthlyAmount || 0), 0), [data.subscriptions]);
  const openTickets = data.supportTickets.filter((x) => !['closed', 'resolved'].includes(x.status)).length;

  return (
    <>
      <Hero eyebrow="SAAS COMMAND CENTER" title={`Welcome back, ${profile?.name || 'Super Admin'}`} subtitle="Control every coaching center, subscription, plan and platform setting from one isolated Super Admin workspace."
        chips={[settings.platformName || 'CoachSphere', `${activeCenters} active centers`, 'Separate Super Admin login']}
        stats={[{ icon: '🏫', label: 'CENTERS', value: data.institutes.length }, { icon: '✅', label: 'ACTIVE', value: activeCenters }, { icon: '💎', label: 'SUBSCRIPTIONS', value: activeSubscriptions }, { icon: '💰', label: 'MRR', value: money(mrr, settings.currency || 'INR') }]}
        actions={[{ label: 'Open Centers', icon: '🏫', primary: true, onClick: () => navigate('/superadmin/centers') }, { label: 'Platform Settings', icon: '⚙️', onClick: () => navigate('/superadmin/settings') }]} />
      <SectionCard icon="🧭" title="Platform Command Centers" subtitle="Operational control areas for your complete coaching SaaS.">
        <div className="command-grid four">
          {[
            ['🏫','Coaching Centers','Create institutes, center admins and manage tenant status.','CENTERS','/superadmin/centers','blue'],
            ['💎','Plans & Subscriptions','Configure packages, limits and recurring subscription records.','COMMERCIAL','/superadmin/plans','purple'],
            ['🎧','Support Operations','Review coaching-center support tickets and resolution status.','SUPPORT','/superadmin/support','green'],
            ['⚙️','Platform Settings','Global logo, colors, support identity, trial defaults and maintenance mode.','PLATFORM','/superadmin/settings','orange'],
          ].map(([icon,title,description,tag,path,accent]) => <button key={title} className={`command-card command-${accent}`} onClick={() => navigate(path)}><div className="command-top"><span className="command-icon">{icon}</span><small>{tag}</small></div><h3>{title}</h3><p>{description}</p><div className="command-foot"><span>Super Admin only</span><b>Open Dashboard →</b></div></button>)}
        </div>
      </SectionCard>
      <SectionCard icon="📊" title="Platform Overview" subtitle="Live counts across the SaaS control plane.">
        <div className="stats-grid">
          <StatCard icon="🏫" label="Centers" value={data.institutes.length} accent="blue" onClick={() => navigate('/superadmin/centers')} />
          <StatCard icon="✅" label="Active Centers" value={activeCenters} accent="green" />
          <StatCard icon="💎" label="Plans" value={data.plans.length} accent="purple" onClick={() => navigate('/superadmin/plans')} />
          <StatCard icon="🧾" label="Subscriptions" value={data.subscriptions.length} accent="orange" onClick={() => navigate('/superadmin/subscriptions')} />
          <StatCard icon="🎧" label="Open Tickets" value={openTickets} accent="red" onClick={() => navigate('/superadmin/support')} />
        </div>
      </SectionCard>
    </>
  );
}
